import { useEffect, useState } from 'react';
import { useParams } from 'wouter';
import { useNav } from '@/lib/nav';
import routes from '@/lib/routes';
import Header from '@/components/layout/header';
import { Loader2 } from 'lucide-react';
import { useAuthStore } from '@/store/auth';
import { useAuth } from '@/auth/AuthProvider';
import { useProjectsStore } from '@/store/projects';
import { useQuery } from '@tanstack/react-query';
import type { FileObject } from '@shared/schema';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronRight } from 'lucide-react';
import ProjectGallerySection from '@/components/projects/project-gallery-section';
import ProjectDataLibrary from '@/components/projects/project-data-library';
import { authorizedFetch, scopedKey } from '@/lib/queryClient';

function mapProjectId(id?: string) {
  if (!id) return id as any;
  // No more demo project IDs - use real project IDs
  return id;
}

export default function ProjectPage() {
  const { projectId: rawId } = useParams();
  const projectId = mapProjectId(rawId);
  // location not needed; useNav handles navigation
  const { go } = useNav();
  const { isLoading } = useAuthStore();
  const { user } = useAuth();
  const { currentProject, setCurrentProject } = useProjectsStore();
  const [hash, setHash] = useState<string>(typeof window !== 'undefined' ? window.location.hash : '');

  // Load files for project (skip until user known)
  const { data: files = [], refetch: refetchFiles } = useQuery<FileObject[]>({
    queryKey: scopedKey([user?.id, '/api/projects', projectId, 'files']),
    enabled: !!projectId && !!user && !isLoading,
  });

  useEffect(() => {
    if (isLoading) return;
    if (!user) {
      go(routes.home());
      return;
    }

    if (projectId) {
      // Fetch project
      authorizedFetch(`/api/projects/${projectId}`)
        .then((res) => res.json())
        .then((project) => setCurrentProject(project))
        .catch(() => go(routes.dashboard()));
    }
  }, [projectId, user, isLoading, go, setCurrentProject]);

  useEffect(() => {
    const handleHashChange = () => setHash(typeof window !== 'undefined' ? window.location.hash : '');
    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="p-6" role="status" aria-busy="true" aria-live="polite">
          <div className="inline-flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm font-medium">Loading</span>
          </div>
        </div>
      </div>
    );
  }
  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="container mx-auto px-6 py-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
          <Button variant="ghost" size="sm" onClick={() => go(routes.dashboard())}>
            Projects
          </Button>
          <ChevronRight className="h-4 w-4" />
          <span className="text-foreground">{currentProject?.name || 'Project'}</span>
        </div>

        {/* Gallery (hidden when viewing #datasets) */}
        {hash !== '#datasets' && (
          <div className="mt-8">
            <ProjectGallerySection 
              files={files}
              projectId={projectId as string}
              onView={(id) => go(routes.plotEditor.open(projectId as string, id) + '?tab=plot')}
              onEdit={(id) => go(routes.plotEditor.open(projectId as string, id))}
            />
          </div>
        )}

        {/* Data Library */}
        <div className="mt-8" id="datasets">
          <Card className="p-4">
            <ProjectDataLibrary 
              projectId={projectId as string}
              files={files}
              onRefetch={refetchFiles}
            />
          </Card>
        </div>
      </div>
    </div>
  );
}
